import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';
import './TeamManagement.css';

interface League {
  id: string;
  name: string;
  rules?: Record<string, any>;
  teamIds: string[];
  administratorId: string;
  registrationFee?: number;
  guestFee?: number;
}

interface Season {
  id: string;
  leagueId: string;
  name: string;
  startDate: string;
  endDate: string;
  isDraft?: boolean;
}

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  status: string;
}

const LeagueManagement: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [league, setLeague] = useState<League | null>(null);
  const [leagueSeasons, setLeagueSeasons] = useState<Season[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'teams' | 'rules' | 'schedule'>('overview');
  const [showAddTeamForm, setShowAddTeamForm] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    registrationFee: '',
    guestFee: ''
  });

  useEffect(() => {
    // Only admins can manage leagues
    if (user && !isAdminOrSuperAdmin(user.profileType)) {
      navigate('/leagues');
      return;
    }
    
    if (id) {
      fetchLeague();
      fetchAllTeams();
    }
  }, [id, user, navigate]);

  const fetchLeague = async () => {
    try {
      const response = await api.get(`/leagues/${id}`);
      setLeague(response.data);
      setEditForm({
        name: response.data.name,
        registrationFee: response.data.registrationFee?.toString() || '',
        guestFee: response.data.guestFee?.toString() || ''
      });
      
      // Fetch seasons for this league
      try {
        const seasonsRes = await api.get('/seasons', { params: { page: '1', limit: '100' } });
        const allSeasons = seasonsRes.data.seasons || seasonsRes.data || [];
        const leagueSeasonsList = allSeasons.filter((s: Season) => s.leagueId === id && !s.isDraft);
        setLeagueSeasons(leagueSeasonsList);
      } catch (err) {
        console.error('Error fetching seasons:', err);
      }
    } catch (error) {
      console.error('Error fetching league:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTeams = async () => {
    try {
      const response = await api.get('/teams', { params: { page: '1', limit: '100' } });
      const teams = response.data.teams || response.data;
      setAllTeams(teams.filter((t: Team) => t.status === 'approved'));
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleAddTeam = async () => {
    if (!selectedTeamId || !league) return;

    try {
      const updatedTeamIds = [...league.teamIds, selectedTeamId];
      await api.put(`/leagues/${id}`, {
        ...league,
        teamIds: updatedTeamIds
      });
      setSelectedTeamId('');
      setShowAddTeamForm(false);
      fetchLeague();
      alert('Team added to league successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to add team');
    }
  };

  const handleRemoveTeam = async (teamId: string) => {
    if (!league || !window.confirm('Remove this team from the league?')) return;

    try {
      const updatedTeamIds = league.teamIds.filter(id => id !== teamId);
      await api.put(`/leagues/${id}`, {
        ...league,
        teamIds: updatedTeamIds
      });
      fetchLeague();
      alert('Team removed from league successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to remove team');
    }
  };

  const handleUpdateRules = async (rules: Record<string, any>) => {
    if (!league) return;

    try {
      await api.put(`/leagues/${id}`, {
        ...league,
        rules
      });
      fetchLeague();
      alert('League rules updated successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update rules');
    }
  };

  const handleUpdateInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!league) return;

    try {
      await api.put(`/leagues/${id}`, {
        ...league,
        name: editForm.name,
        registrationFee: editForm.registrationFee ? parseFloat(editForm.registrationFee) : null,
        guestFee: editForm.guestFee ? parseFloat(editForm.guestFee) : null
      });
      setIsEditingInfo(false);
      fetchLeague();
      alert('League information updated successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update league information');
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!league) {
    return <div className="container">League not found</div>;
  }

  if (!isAdminOrSuperAdmin(user?.profileType)) {
    return <div className="container">Access denied. Admin only.</div>;
  }

  const leagueTeams = allTeams.filter(t => league.teamIds.includes(t.id));
  const availableTeams = allTeams.filter(t => !league.teamIds.includes(t.id));

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>{league.name}</h1>
          {leagueSeasons.length > 0 && (() => {
            const latestSeason = leagueSeasons
              .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];
            return (
              <p style={{ color: '#757575' }}>
                Current Season: {latestSeason.name} ({new Date(latestSeason.startDate).toLocaleDateString()} - {new Date(latestSeason.endDate).toLocaleDateString()})
              </p>
            );
          })()}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/leagues')}>
          Back to Leagues
        </button>
      </div>

      <div className="tabs" style={{ marginBottom: '20px' }}>
        <button className={activeTab === 'overview' ? 'tab active' : 'tab'} onClick={() => setActiveTab('overview')}>
          Overview
        </button>
        <button className={activeTab === 'teams' ? 'tab active' : 'tab'} onClick={() => setActiveTab('teams')}>
          Teams ({league.teamIds.length})
        </button>
        <button className={activeTab === 'rules' ? 'tab active' : 'tab'} onClick={() => setActiveTab('rules')}>
          Rules
        </button>
        <button className={activeTab === 'schedule' ? 'tab active' : 'tab'} onClick={() => setActiveTab('schedule')}>
          Schedule
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>League Information</h2>
              <button 
                className="btn btn-primary" 
                onClick={() => setIsEditingInfo(!isEditingInfo)}
              >
                {isEditingInfo ? 'Cancel Edit' : 'Edit Information'}
              </button>
            </div>

            {isEditingInfo ? (
              <form onSubmit={handleUpdateInfo}>
                <div className="form-group">
                  <label>League Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                    style={{ width: '100%', padding: '10px' }}
                  />
                </div>
                <div className="form-group">
                  <label>Registration Fee ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.registrationFee}
                    onChange={(e) => setEditForm({ ...editForm, registrationFee: e.target.value })}
                    placeholder="e.g., 75.00"
                    style={{ width: '100%', padding: '10px' }}
                  />
                  <small style={{ color: '#757575', fontSize: '12px' }}>Per-player fee charged when joining a team in this league</small>
                </div>
                <div className="form-group">
                  <label>Guest Fee ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.guestFee}
                    onChange={(e) => setEditForm({ ...editForm, guestFee: e.target.value })}
                    placeholder="e.g., 25.00"
                    style={{ width: '100%', padding: '10px' }}
                  />
                  <small style={{ color: '#757575', fontSize: '12px' }}>Fee charged to guest players when accepting an invite</small>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsEditingInfo(false);
                      fetchLeague(); // Reset form
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <p><strong>League Name:</strong> {league.name}</p>
                <p><strong>Registration Fee:</strong> {league.registrationFee ? `$${league.registrationFee.toFixed(2)}` : 'None'}</p>
                <p><strong>Guest Fee:</strong> {league.guestFee ? `$${league.guestFee.toFixed(2)}` : 'None'}</p>
                <p><strong>Number of Teams:</strong> {league.teamIds.length}</p>
                {leagueSeasons.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h3>Seasons</h3>
                    {leagueSeasons
                      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                      .map((season) => (
                        <div key={season.id} style={{ marginBottom: '10px', padding: '10px', background: '#F5F5F5', borderRadius: '8px' }}>
                          <p style={{ margin: '5px 0' }}><strong>{season.name}</strong></p>
                          <p style={{ margin: '5px 0', color: '#757575', fontSize: '14px' }}>
                            {new Date(season.startDate).toLocaleDateString()} - {new Date(season.endDate).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                  </div>
                )}
                {league.rules && (
                  <div style={{ marginTop: '20px' }}>
                    <h3>League Rules</h3>
                    {league.rules.maxPlayers && <p><strong>Max Players:</strong> {league.rules.maxPlayers}</p>}
                    {league.rules.minPlayers && <p><strong>Min Players:</strong> {league.rules.minPlayers}</p>}
                    {league.rules.matchDuration && <p><strong>Match Duration:</strong> {league.rules.matchDuration} minutes</p>}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'teams' && (
          <div>
            <div className="card" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>League Teams</h2>
                {availableTeams.length > 0 && (
                  <button className="btn btn-primary" onClick={() => setShowAddTeamForm(!showAddTeamForm)}>
                    {showAddTeamForm ? 'Cancel' : 'Add Team'}
                  </button>
                )}
              </div>

              {showAddTeamForm && (
                <div style={{ marginTop: '20px', padding: '15px', background: '#F5F5F5', borderRadius: '8px' }}>
                  <div className="form-group">
                    <label>Select Team to Add</label>
                    <select
                      value={selectedTeamId}
                      onChange={(e) => setSelectedTeamId(e.target.value)}
                      style={{ width: '100%', padding: '10px', marginBottom: '10px' }}
                    >
                      <option value="">Choose a team...</option>
                      {availableTeams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} ({team.teamNumber})
                        </option>
                      ))}
                    </select>
                  </div>
                  <button className="btn btn-primary" onClick={handleAddTeam} disabled={!selectedTeamId}>
                    Add Team
                  </button>
                </div>
              )}
            </div>

            <div className="card">
              {leagueTeams.length === 0 ? (
                <p>No teams assigned to this league yet.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Team Name</th>
                      <th>Team Number</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leagueTeams.map((team) => (
                      <tr key={team.id}>
                        <td>
                          <Link to={`/teams/${team.id}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                            {team.name}
                          </Link>
                        </td>
                        <td>{team.teamNumber}</td>
                        <td>
                          <button
                            className="btn btn-danger"
                            style={{ fontSize: '12px', padding: '5px 10px' }}
                            onClick={() => handleRemoveTeam(team.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="card">
            <h2>League Rules</h2>
            <RulesEditor
              currentRules={league.rules || {}}
              onSave={handleUpdateRules}
            />
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="card">
            <h2>League Schedule</h2>
            <p>Schedule management will be available here.</p>
            <Link to={`/matches?leagueId=${id}`} className="btn btn-primary">
              View League Matches
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

interface RulesEditorProps {
  currentRules: Record<string, any>;
  onSave: (rules: Record<string, any>) => void;
}

const RulesEditor: React.FC<RulesEditorProps> = ({ currentRules, onSave }) => {
  const [rules, setRules] = useState({
    maxPlayers: currentRules.maxPlayers?.toString() || '',
    minPlayers: currentRules.minPlayers?.toString() || '',
    matchDuration: currentRules.matchDuration?.toString() || '',
    guestPlayerLimit: currentRules.guestPlayerLimit?.toString() || ''
  });

  const handleSave = () => {
    const rulesObj: Record<string, any> = {};
    if (rules.maxPlayers) rulesObj.maxPlayers = parseInt(rules.maxPlayers);
    if (rules.minPlayers) rulesObj.minPlayers = parseInt(rules.minPlayers);
    if (rules.matchDuration) rulesObj.matchDuration = parseInt(rules.matchDuration);
    if (rules.guestPlayerLimit) rulesObj.guestPlayerLimit = parseInt(rules.guestPlayerLimit);
    onSave(rulesObj);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
        <div className="form-group">
          <label>Max Players per Team</label>
          <input
            type="number"
            value={rules.maxPlayers}
            onChange={(e) => setRules({ ...rules, maxPlayers: e.target.value })}
            min="1"
            placeholder="e.g., 20"
          />
        </div>

        <div className="form-group">
          <label>Min Players per Team</label>
          <input
            type="number"
            value={rules.minPlayers}
            onChange={(e) => setRules({ ...rules, minPlayers: e.target.value })}
            min="1"
            placeholder="e.g., 11"
          />
        </div>

        <div className="form-group">
          <label>Match Duration (minutes)</label>
          <input
            type="number"
            value={rules.matchDuration}
            onChange={(e) => setRules({ ...rules, matchDuration: e.target.value })}
            min="1"
            placeholder="e.g., 90"
          />
        </div>

        <div className="form-group">
          <label>Guest Player Game Limit</label>
          <input
            type="number"
            value={rules.guestPlayerLimit}
            onChange={(e) => setRules({ ...rules, guestPlayerLimit: e.target.value })}
            min="0"
            placeholder="e.g., 3"
          />
          <small style={{ color: '#757575', fontSize: '12px' }}>Max games per guest player. Leave empty for no limit.</small>
        </div>
      </div>

      <button className="btn btn-primary" onClick={handleSave}>
        Save Rules
      </button>
    </div>
  );
};

export default LeagueManagement;

